/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { FlatList, Pressable, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { webOperationalScrollBindings } from '../../shared/theme/webScrollbar';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { BranchStockActivityRow } from '../../inventory/stock/public-api';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { BRANCH_ACTIVITY_FETCH_LIMIT } from '../../inventory/stock/public-api';
import { BranchActivityRowView, type BranchLike } from './BranchActivityRow';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';

/**
 * Feed de actividad de sucursal. Objeto (no array plano) para poder
 * distinguir error de carga vs. vacío real vs. acceso restringido
 * (historial de equipo = solo dueño de la organización).
 * El wiring intermedio (`useInventoryScreen` / `InventoryScreenModals`)
 * pasa el objeto tal cual.
 */
export type BranchActivityFeed = {
  rows: BranchStockActivityRow[];
  /** Error de carga — mostrar panel con retry, nunca "Sin actividad". */
  error: string | null;
  /** true cuando el usuario no es dueño: el detalle remoto es owner-only. */
  restricted: boolean;
  retry: () => void;
};

type Props = {
  visible: boolean;
  branchActivity: BranchActivityFeed;
  branches: BranchLike[];
  layoutWide?: boolean;
  onClose: () => void;
  onOpenProductStock?: (productId: string) => void;
};

export const BranchActivityModal = ({
  visible,
  branchActivity,
  branches,
  layoutWide = false,
  onClose,
  onOpenProductStock,
}: Props) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width: winW, height: winH } = useWindowDimensions();
  const isMobile = winW < 768;
  const insets = useSafeAreaInsets();

  const rows = branchActivity.rows;
  const hasError = branchActivity.error != null;
  const restricted = branchActivity.restricted === true;

  const listMaxHeight = useMemo(() => {
    if (layoutWide) return Math.min(520, Math.max(280, winH * 0.5));
    return Math.min(400, Math.max(240, winH * 0.38));
  }, [layoutWide, winH]);

  const modalCardStyle = useMemo(
    () =>
      isMobile
        ? [styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm), maxHeight: winH * 0.92 }]
        : [
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthStandard,
            layoutWide && styles.modalCardWide,
            {
              maxHeight: winH * 0.92,
              maxWidth: layoutWide ? Math.min(640, winW - 48) : Math.min(480, winW - 32),
            },
          ],
    [isMobile, insets, winH, winW, layoutWide]
  );

  const countSubtitle = restricted
    ? 'Actividad de la organización'
    : 'Movimientos de esta sucursal';

  const countLabel =
    rows.length >= BRANCH_ACTIVITY_FETCH_LIMIT
      ? `últimos ${BRANCH_ACTIVITY_FETCH_LIMIT}`
      : 'movimientos';

  const rowHint =
    onOpenProductStock && rows.length > 0
      ? 'Las filas abren la ficha del producto.'
      : null;

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={onClose}>
        <Pressable style={[posUi.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]} onPress={isMobile ? onClose : undefined}>
          <Pressable style={modalCardStyle} onPress={(e) => e.stopPropagation()}>
          {isMobile ? <View style={styles.grabber} /> : null}
          <TicketModalHeader
            kicker="Stock · Actividad"
            title="Actividad reciente"
            subtitle={
              hasError || restricted
                ? countSubtitle
                : `${countSubtitle} · ${rows.length} ${countLabel}`
            }
            onClose={onClose}
            compact={isMobile}
          />
          {rowHint ? (
            <Text style={styles.rowHint} numberOfLines={2}>
              {rowHint}
            </Text>
          ) : null}

          {hasError ? (
            <ModuleEmptyState
              title="No pudimos cargar la actividad"
              subtitle={branchActivity.error ?? undefined}
              primaryAction={{ label: 'Reintentar', onPress: branchActivity.retry, testID: 'branchActivity.retry' }}
            />
          ) : restricted ? (
            <ModalStatePanel
              empty
              emptyTitle="Actividad para el dueño"
              emptyMessage="La actividad detallada la ve el dueño de la organización."
            />
          ) : rows.length === 0 ? (
            <ModalStatePanel
              empty
              emptyTitle="Sin actividad"
              emptyMessage="No hay movimientos recientes en esta sucursal."
            />
          ) : (
            <View style={[modalExperience.listContainer, { maxHeight: listMaxHeight }]}>
              <FlatList
                {...webOperationalScrollBindings(styles.historyFlatList)}
                data={rows}
                keyExtractor={(item) => `${item.type}-${item.id}`}
                keyboardShouldPersistTaps="handled"
                renderItem={({ item }) => (
                  <BranchActivityRowView
                    item={item}
                    branches={branches}
                    detailNumberOfLines={layoutWide ? 3 : 2}
                    onPress={
                      onOpenProductStock && item.productId
                        ? () => onOpenProductStock(item.productId)
                        : undefined
                    }
                  />
                )}
                style={styles.historyFlatList}
              />
            </View>
          )}
          </Pressable>
        </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetCard: {
    width: '100%',
    maxHeight: '92%',
    backgroundColor: ticketColors.paper2,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingTop: 8,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginBottom: 4,
  },
  modalCardWide: {
    width: '100%',
  },
  rowHint: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '600',
    marginTop: 2,
    marginBottom: 4,
  },
  historyFlatList: {
    flex: 1,
  },
});
